import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Types
import { ShowOTActionProps } from './types';
import { variant } from '../../SpecialTag/types';
import { isBasicAction } from '../../../../utils/order';

// Styles Components
import {
  ContainerContent,
  ContainerData,
  ContainerDescription,
  ContainerHeader,
  ContainerRefIcon,
  ContainerTitleArrow,
  DateContainer,
  InputAction,
  InputDescription,
  InputOperaris,
  RefRoid,
  RightArrow,
  SwitchButton,
  TagsContainer,
  TitleDate,
  TitleOrder,
  ActionLocationGroup,
} from '../MainStyledComponents';

// Components
import { SpecialTag } from '../../SpecialTag';
import { WorkedInRoutePills } from '../WorkedInRoutePills';
import { UnlinkedRoutePills } from '../UnlinkedRoutePills';
import { TagActionsMenu } from '../../SpecialTag/TagActionsMenu';
import { DateTime } from '../../../inputs/DateTime';

// Icons
import ArrowRight from '../../../../assets/icons/triangle-right.svg';

// GraphQL
import { client } from '../../../../gql/client';
import {
  GET_ACTION_BY_ID,
  GET_ACTION_CONVERT_INFO,
} from '../../../../gql/queries/actions/actions';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';

// Utils
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../forms/ActionForms/constants';
import { externalCompaniesNaming } from '../../tables/formTables/ExternalCompaniesSelectionTable/constants';
import { formAccidentNaming } from '../../../forms/AddBasicInfoAccidentForm/constants';
import { formNaming as formCommunicationNaming } from '../../../forms/NewCommunicationForm/constants';

// Media
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { ContainerOTStyled, ButtonViewDetail } from './styles';
import { dataTransferNaming } from '../../../forms/ActionForms/constants';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../modals/SuperModal/types';
import { OrderState, OrderType } from '../../../../types/orders';
import { actionTabsUnlockAll } from '../../../../state/actions/ui/actionTabs';
import { roidOTSelected } from '../../../../state/actions/ui/roidOTSelectedGeneralView';
import { withNavigationFeedback } from '../../../../utils/navigationFeedback';
import { useAuth } from '../../../../hooks/useAuth';
import {
  canConvertAction,
  canTransferAction,
} from '../../../../utils/convertAction';
import { rememberChildCardAnchor } from '../../../../utils/childCardAnchor';
import {
  buildActionSummaryPath,
  getSurveillanceRouteIdFromPath,
} from '../../../../utils/surveillanceRoute';

export const ActionOT = ({
  roid,
  tag,
  date,
  endDate,
  operators,
  capitol,
  capitols,
  subCapitol,
  subCapitolsValues,
  predefine,
  predefinedOrders,
  laneCutting,
  description,
  notes,
  pkInit,
  pkEnd,
  isTransferred = false,
  id,
  actionID,
  parentOrder,
  revalidatorFunction,
  state,
  stateParentExpeditionOrder,
  userName,
  registeredBy,
  imageIDs,
  workedInRoutes,
  unlinkedRoutes,
  containerType,
  linkedSegmentRoid,
  onRelateClick,
  onUnlinkClick,
  initiallyExpanded,
}: ShowOTActionProps): JSX.Element => {
  // La básica del RV no tiene tripulación: los operarios son del recorrido
  // (spec 2026-08-05). Enseñar aquí una caja «Operarios» vacía era mentir por
  // omisión; desde 2026-09-04 no se pinta. Se decide por el DATO, con el tipo
  // de la Order padre como respaldo cuando la card no trae `containerType`.
  const isBasic = isBasicAction({
    containerType,
    parentOrderType: parentOrder?.type,
  });
  const { t } = useTranslation(['common', 'forms', 'pages']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Sala no transfiere actuaciones (el back se lo niega): no se le pinta la
  // pastilla «Transferir» (W4, 15-oct). La «Transferida» sí, es información.
  const canTransfer = canTransferAction(user?.user?.rolActive);
  const [showInfo, setShowInfo] = useState(initiallyExpanded ?? false);

  // Bug descripciones (2026-06-05): la tarjeta de la actuación en la OT genérica
  // mostraba order.description crudo (vía {...order}). Mostramos "validada || operario"
  // para ser coherentes con el resto de vistas. La validada vive en Action (no en el
  // fragment NEW_ORDER), así que la traemos por actionID (cache-first, sin red si ya está).
  const [effectiveDescription, setEffectiveDescription] = useState(description);
  useEffect(() => {
    let cancelled = false;
    if (!actionID) {
      setEffectiveDescription(description);
      return;
    }
    client
      .query({
        query: GET_ACTION_BY_ID,
        variables: { id: actionID },
        fetchPolicy: 'cache-first',
      })
      .then(({ data }) => {
        if (!cancelled) {
          setEffectiveDescription(data?.action?.descriptionValidated || description);
        }
      })
      .catch(() => {
        if (!cancelled) setEffectiveDescription(description);
      });
    return () => {
      cancelled = true;
    };
  }, [actionID, description]);
  const toggleInfo = useCallback(() => setShowInfo(!showInfo), [showInfo]);
  const isLandscapeTablet = useIsLandscapeTablet();
  const namingOperators =
    operators?.map(
      (operator: { name: string; surName: string }) =>
        `${operator.name} ${operator.surName}`
    ) ?? [];

  const openModalTransferSubOrder = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();

    // «Convertir actuación» (QA T5): si gestión podría pasarla al otro mundo,
    // antes de abrir se pregunta si está validada y si lleva trabajos
    // externos o fauna (la tarjeta no lo sabe). Sin respuesta (sin red), no se
    // ofrece convertir: el modal sale como siempre.
    const roleActive = user?.user?.rolActive;
    let convertInfo: {
      isValidate: boolean | null;
      hasExternalOrFauna: boolean;
    } | null = null;
    if (actionID && canConvertAction(roleActive, state, false)) {
      try {
        const { data } = await client.query({
          query: GET_ACTION_CONVERT_INFO,
          variables: { id: actionID },
          fetchPolicy: 'network-only',
        });
        const info = data?.action;
        if (info) {
          convertInfo = {
            isValidate: info.isValidate ?? false,
            hasExternalOrFauna:
              info.isExternal === true ||
              (info.externalWorksIDs?.length ?? 0) > 0 ||
              (info.animalUnitIDs?.length ?? 0) > 0,
          };
        }
      } catch {
        convertInfo = null;
      }
    }

    dispatch(
      openModal(modalIds.TRANSFER_SUBORDER_MODAL, {
        namingPersistForm: dataTransferNaming.SUB_ORDER_ACTION,
        orderToTransfer: {
          id,
          type: OrderType.Action,
          orderParent: parentOrder.id,
          // «Convertir actuación» (W4): el modal ofrece a gestión el destino
          // del otro mundo si la actuación aún se puede convertir, y lo cuenta
          // en una frase (ROID, tramo ligado).
          state,
          roid,
          linkedSegmentRoid,
          // Con el recorrido en curso no se convierte (revisión W4, M-8).
          parentState: stateParentExpeditionOrder,
          // Una básica sin hora de fin se avisa al convertirla (M-7).
          endDate,
          // Sin la respuesta de arriba (null) no se ofrece convertir.
          isValidate: convertInfo ? convertInfo.isValidate : null,
          hasExternalOrFauna: convertInfo?.hasExternalOrFauna ?? false,
        },
        currentParentOrder: {
          id: parentOrder.id,
          type: parentOrder.type,
        },
        revalidatorFunction: revalidatorFunction,
      })
    );
  };

  // Handler para navegar al detalle de la actuación
  const handleViewDetail = useCallback(() =>
    withNavigationFeedback(async () => {
    try {
      // BUG FIX: Limpiar contextos de otras entidades antes de setear el de acción
      sessionStorage.removeItem(formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT);
      sessionStorage.removeItem(formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT);

      // Ancla de vuelta: al volver, `ShowOT` traerá esta tarjeta a la vista y la
      // dejará desplegada. Cubre las dos puertas — el botón «Volver» (que hace
      // PUSH) y la flecha atrás del navegador (POP).
      rememberChildCardAnchor(parentOrder?.id, roid);

      // BUG #18 FIX: Usar actionID (Action ID) en vez de id (Order ID)
      // Consultar datos de la actuación
      const {
        data: { action },
      } = await client.query({
        query: GET_ACTION_BY_ID,
        variables: { id: actionID },
      });

      // Consultar datos de la orden
      const {
        data: { order },
      } = await client.query({
        query: GET_ORDER_BY_ID,
        variables: { id: action.orderIDs[0] },
      });

      // Limpiar sessionStorage relevante
      Object.values({
        ...formActionNaming,
        ...formActionValidationNaming,
        ...externalCompaniesNaming,
      }).forEach((key) => {
        if (key !== formActionNaming.NEW_ACTION_DATA) {
          sessionStorage.removeItem(key);
        }
      });

      // Guardar datos en sessionStorage con isActionEdited: 'false'
      // BUG #19 FIX: Usar actionID (Action ID) en vez de id (Order ID)
      // para que los loaders downstream puedan consultar la acción correctamente
      sessionStorage.setItem(
        formActionNaming.NEW_VALIDATION_ACTION_DATA,
        compressSessionStorage(
          JSON.stringify({
            id: actionID,
            orderId: order.id,
            roid: order.roid,
            isActionEdited: 'false',
            origin: 'workorder',
            parentOrderId: parentOrder.id,
            parentRoid: parentOrder.roid,
            parentOrderType: parentOrder.type, // Parent order type for campaign detection
            entityRoid: roid,
            returnTo:
              parentOrder?.type === OrderType.Campaign
                ? `/work-orders/campaign/${parentOrder.id}`
                : `/work-orders/${parentOrder.id}`,
          })
        )
      );

      // FLUJO VALIDACIÓN: Desbloquear TODAS las tabs
      dispatch(actionTabsUnlockAll());

      // Dispatch roidOTSelected to update Redux state for breadcrumb switch case
      // Include navigation metadata for breadcrumb context (Redux backup for sessionStorage)
      if (actionID) {
        dispatch(roidOTSelected('actions-validation', roid, actionID, undefined, undefined, {
          origin: 'workorder',
          parentOrderId: parentOrder.id,
          parentRoid: parentOrder.roid,
          entityRoid: roid,
        }));
      }

      // Sin id de actuación no hay destino: antes se navegaba igual y salía un
      // `/summary/undefined` que el loader resuelve a "no encontrada". El resto
      // del handler tampoco funciona sin él (la query de arriba lo usa).
      if (!actionID) return;

      // Context-aware navigation: detect if parent is a campaign
      const isCampaign = parentOrder?.type === OrderType.Campaign;

      // ¿Esta card se está pintando DENTRO de un recorrido? Se mira la URL, no
      // la Order padre: `parentOrder.id` de una básica es el id de la Order
      // GEMELA del recorrido, y la ruta `/surveillance-route/:routeId` quiere
      // el de la entidad `SurveillanceRoute` — confundirlos da un 404 mudo
      // (misma trampa de entidad doble que documenta `ActionSummary`). La
      // pantalla que nos monta ya está bajo el id bueno (2026-08-15).
      const surveillanceRouteId = getSurveillanceRouteIdFromPath(
        window.location.pathname
      );

      if (surveillanceRouteId) {
        // Navigate within the surveillance route context — show summary first
        navigate(buildActionSummaryPath(actionID, surveillanceRouteId));
      } else if (isCampaign && parentOrder?.id) {
        // Navigate within campaign context — show summary first
        navigate(`/work-orders/campaign/${parentOrder.id}/actions-validation/summary/${actionID}`);
      } else {
        // Navigate to global action route — show summary first
        navigate(buildActionSummaryPath(actionID));
      }
    } catch (error) {
      console.error('Error navigating to view detail:', error);
    }
  }), [actionID, dispatch, id, navigate, parentOrder.id, parentOrder.type, roid]);

  return (
    <ContainerOTStyled data-child-roid={roid}>
      <ContainerHeader onClick={toggleInfo}>
        <TitleOrder>
          {t('orderTitles.action')}
          {/* Fix "a 01:00" (2026-07-21): sin fecha final NO se pinta la hora
              final — antes `format(new Date(null))` = época 1970 = 01:00 CET.
              Afecta a todas las At. sin cerrar (básicas de RV incluidas). */}
          <TitleDate>{` ${format(new Date(date), 'dd/MM/yyyy - HH:mm')}${
            endDate ? ` a ${format(new Date(endDate), 'HH:mm')}` : ''
          }`}</TitleDate>
        </TitleOrder>
        <ContainerTitleArrow>
          <ContainerRefIcon>
            <RefRoid>{roid}</RefRoid>
            <TagsContainer>
              <SpecialTag
                variant={tag}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
              />
              {(isTransferred ||
                (canTransfer &&
                  state !== OrderState.Annulled &&
                  stateParentExpeditionOrder !== OrderState.Annulled &&
                  stateParentExpeditionOrder !== OrderState.End)) && (
                <SpecialTag
                  variant={isTransferred ? variant.Transferred : variant.Transfer}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  onClick={
                    !isTransferred
                      ? (e) => openModalTransferSubOrder(e)
                      : undefined
                  }
                />
              )}
              {/* Spec 2026-09-23: el recorrido cuya patrulla hizo esta hija. */}
              <WorkedInRoutePills routes={workedInRoutes} />
              {/* Spec § 16: una patrulla SIN vincular la hizo → aviso. */}
              <UnlinkedRoutePills
                routes={unlinkedRoutes}
                parentOrderId={parentOrder.id}
                revalidatorFunction={revalidatorFunction}
              />
              {/* P15 rework bidireccional (2026-07-09): recíproco de la pill
                  de RouteSegmentOT. Solo renderiza si el consumidor aporta
                  `onRelateClick` (únicamente `RouteChildOrders`, contexto RV).
                  Vinculada → pill VERDE "Vinculat amb Tr.NN" con editar (✎)
                  inline (reabre el modal para re-vincular). No lleva "ver"
                  porque el tram no tiene página propia (vive dentro del detalle
                  del RV). Sin vincular → CTA garnet que abre el modal
                  elige-tram. */}
              {/* Una actuación ANULADA no resuelve ningún tramo: ni se le
                  ofrece «Vincular a tramo» ni se pinta «Vinculado con».
                  Anular suelta el tramo en el backend (back, 2026-10-08); si
                  el dato aún lo conserva, el tramo ya se ofrece pendiente. */}
              {onRelateClick &&
                state !== OrderState.Annulled &&
                (linkedSegmentRoid ? (
                  // Una sola acción (editar) → TagActionsMenu ejecuta el tap
                  // sobre TODA la pill (target táctil máximo, sin menú) —
                  // opción C, feedback usuario 2026-07-10.
                  <TagActionsMenu
                    variant={variant.LinkedToSegment}
                    sizeTag={isLandscapeTablet ? 'small' : 'default'}
                    title={t('pages:surveillance.linkedWith', {
                      roid: linkedSegmentRoid,
                    })}
                    actions={[
                      {
                        icon: 'edit',
                        label: t('common:buttons.edit'),
                        onClick: () => onRelateClick(),
                      },
                      // Opción A (2026-07-10): deshacer el par — el tram
                      // recupera su CTA "Vincular a actuació".
                      ...(onUnlinkClick
                        ? [
                            {
                              icon: 'unlink' as const,
                              label: t('common:buttons.unlink'),
                              onClick: () => onUnlinkClick(),
                            },
                          ]
                        : []),
                    ]}
                  />
                ) : (
                  <SpecialTag
                    variant={variant.LinkCta}
                    sizeTag={isLandscapeTablet ? 'small' : 'default'}
                    title={t('pages:surveillance.linkToSegment')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRelateClick();
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
            </TagsContainer>
          </ContainerRefIcon>
          <RightArrow
            src={ArrowRight}
            alt={t('common:expandInfo')}
            showInfo={showInfo}
          />
        </ContainerTitleArrow>
      </ContainerHeader>
      {showInfo ? (
        <ContainerContent>
          <ContainerData>
            <ActionLocationGroup>
              <DateContainer>
                <DateTime
                  label={t('forms:workOrder.registerDate')}
                  labelHour={t('forms:workOrder.startHour')}
                  isDisabled={true}
                  initialDate={date}
                />
                <DateTime
                  labelHour={t('forms:workOrder.endHour')}
                  onlyHour={true}
                  isDisabled={true}
                  initialDate={endDate}
                />
              </DateContainer>
              {userName && (
                <InputAction
                  label={t('forms:workOrder.createdBy')}
                  typeInput="normal"
                  disabled={true}
                  value={userName}
                />
              )}
              {registeredBy && (
                <InputAction
                  label={t('forms:registeredBy.label')}
                  typeInput="normal"
                  disabled={true}
                  value={registeredBy}
                />
              )}
              {!isBasic && (
                <InputOperaris
                  label={t('forms:workOrder.operators')}
                  typeInput="normal"
                  disabled={true}
                  value={namingOperators?.join(', ')}
                />
              )}
              {pkInit && (
                <InputAction
                  label={t('forms:workOrder.pkInitial')}
                  typeInput="normal"
                  disabled={true}
                  value={pkInit}
                />
              )}
              {pkEnd && (
                <InputAction
                  label={t('forms:workOrder.pkFinal')}
                  typeInput="normal"
                  disabled={true}
                  value={pkEnd}
                />
              )}
            </ActionLocationGroup>
            <ActionLocationGroup>
              <InputAction
                label={t('forms:workOrder.predefined')}
                typeInput="normal"
                disabled={true}
                value={
                  predefinedOrders?.find(
                    (predefinedOrder) => predefinedOrder.id === predefine
                  )?.name
                }
              />
              <InputAction
                label={t('forms:workOrder.chapter')}
                typeInput="normal"
                disabled={true}
                value={
                  capitols?.find((capitolInter) => capitolInter.id === capitol)
                    ?.name
                }
              />
              <InputAction
                label={t('forms:workOrder.subchapter')}
                typeInput="normal"
                disabled={true}
                value={
                  subCapitolsValues?.find(
                    (subCapitolInter) => subCapitolInter.id === subCapitol
                  )?.name
                }
              />
              <SwitchButton
                label={t('forms:workOrder.laneCut')}
                currentState={laneCutting}
                isDisabled={true}
              />
            </ActionLocationGroup>
          </ContainerData>
          <ContainerDescription>
            <InputDescription
              label={t('forms:action.actionDescription')}
              disabled={true}
              value={effectiveDescription}
            />
            {notes && (
              <InputDescription
                label={t('forms:workOrder.notes')}
                disabled={true}
                value={notes}
              />
            )}
            {imageIDs && imageIDs.length > 0 && (
              <ButtonViewDetail
                variant="download"
                onClick={() => {
                  dispatch(
                    openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, {
                      selectedRows: [id],
                    })
                  );
                }}
                type="button"
              />
            )}
            <ButtonViewDetail
              variant="visibility"
              onClick={handleViewDetail}
              type="button"
            />
          </ContainerDescription>
        </ContainerContent>
      ) : null}
    </ContainerOTStyled>
  );
};
